import React, { useReducer, useCallback } from 'react';
import { TFileId, TLanguageIDs, IItemForGetShortInfo, IDefaultLanguage, ICreateModifierV2 } from 'types';
import { createModifierInitialState, CreateModifierContext, TTypeOfModalWindow } from './createModifierContext';
import { createModifierReducer } from './createModifierReducer';
import { CreateModifierActionTypes } from './createModifierActions';
import { v4 as uuidv4 } from 'uuid';

export const CreateModifierState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createModifierReducer, createModifierInitialState);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: CreateModifierActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateModifierActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const changeNameOfModifier = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_NAME_OF_MODIFIER,
			payload: {
				nameOfModifier: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: CreateModifierActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const toggleHasSchedulePriority = useCallback(() => {
		dispatch({
			type: CreateModifierActionTypes.TOGGLE_HAS_SCHEDULE_PRIORITY,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateModifierActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: CreateModifierActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateModifierActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateModifierActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: Nullable<ICreateModifierV2>) => {
		dispatch({
			type: CreateModifierActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateModifierActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateModifierActionTypes.RESET_FIELDS }), []);

	return (
		<CreateModifierContext.Provider
			value={{
				setFileId,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				changeNameOfModifier,
				changeDescription,
				changeExternalId,
				generateUniqueExternalId,
				toggleHasSchedulePriority,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				fileId: state.fileId,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				nameOfModifier: state.nameOfModifier,
				description: state.description,
				externalId: state.externalId,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateModifierContext.Provider>
	);
};
